import { Box, Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { TableCell, TableCellContext } from '@/components/lists/TableCell';
import { InfoTooltip } from '@/components/poppers/Tooltip/InfoTooltip';
import { entityKindToDisplayName } from '@/modules/entities/EntityForm/utils';
import { testamentaryEntityKindToDisplayName } from '@/modules/entities/testamentaryEntities/testamentaryEntities.utils';
import { useIRSConstants } from '@/modules/irs/useIRSConstants';
import { containerQuery } from '@/styles/containerQueries';
import {
  DispositiveProvisionRecipientKind,
  DispositiveProvisionTransferTaxKind,
  StateCode,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import {
  formatCurrency,
  formatCurrencyNoDecimals,
} from '@/utils/formatting/currency';

import { useUnguardedDispositiveProvisionsContext } from '../contexts/dispositiveProvisions.context';
import {
  formatAssetDispositionLabel,
  formatDispositionKindCopy,
  getAssetDisplayNames,
  getProvisionAssetCounts,
  getTransferMethodLabel,
  hasOnlyInsurancePolicies,
  isAssetDP,
} from '../dispositiveProvisions.formatting';
import { getTransferTaxKindOptions } from '../DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import { useRemainingExemptionAmountForClient } from '../DispositiveProvisionsForm/hooks/useRemainingExemptionAmountForClient';
import { DispositiveProvisions_DispositiveProvisionFragment } from '../graphql/DispositiveProvisions.fragments.generated';
import { DispositiveProvisionDirection } from './DispositiveProvisionsByDeath.types';

interface ProvisionDetails {
  id: string;
  heading: string;
  subheading?: string;
  tooltip?: string;
}

export interface DispositiveProvisionRow {
  heading: string;
  description: string;
  additionalItems?: string[];
  badgeText?: string;
  badgeTextCallout?: string;
  provisions: ProvisionDetails[];
}

export function getDistributionDetails(
  provision: DispositiveProvisions_DispositiveProvisionFragment,
  calculatedProvisionAmount: Decimal | null,
  opts: { compact?: boolean; proRataTemplateName?: string } = {}
): ProvisionDetails[] {
  if (calculatedProvisionAmount) {
    const currencyFormatter = opts.compact
      ? formatCurrencyNoDecimals
      : formatCurrency;

    const proRataSubheading = opts.proRataTemplateName
      ? `${opts.proRataTemplateName} (pro rata)`
      : undefined;

    // For asset DPs, still show the transfer method label even when we have
    // a calculated amount so the user knows this is an asset-level disposition.
    const subheading =
      proRataSubheading ??
      (isAssetDP(provision) ? getTransferMethodLabel(provision) : undefined);

    return [
      {
        id: provision.id,
        heading: currencyFormatter(calculatedProvisionAmount),
        subheading,
      },
    ];
  }

  // When pro rata is enabled and we don't have a calculated amount,
  // show the template name and "(pro rata)" as subheading.
  const proRataSubheading = opts.proRataTemplateName
    ? `${opts.proRataTemplateName} (pro rata)`
    : undefined;

  // Asset DPs get their own formatting (e.g. "100% of Property A")
  if (isAssetDP(provision)) {
    return [
      {
        id: provision.id,
        heading: formatAssetDispositionLabel({
          pct: provision.dispositionPercentage ?? new Decimal(100),
          assetNames: getAssetDisplayNames(provision),
          transferMethod: provision.transferMethod,
          isInsuranceOnly: hasOnlyInsurancePolicies(
            getProvisionAssetCounts(provision)
          ),
        }),
        subheading: proRataSubheading ?? getTransferMethodLabel(provision),
      },
    ];
  }

  const heading = formatDispositionKindCopy(
    {
      dispositionKind: provision.dispositionKind,
      dispositionPercentage: provision.dispositionPercentage ?? null,
      dispositionFormulaicPercentage:
        provision.dispositionFormulaicPercentage ?? null,
      dispositionAmount: provision.dispositionAmount ?? null,
      dispositionLimit: provision.dispositionLimit ?? null,
      growByInflation: provision.growByInflation,
    },
    { compact: opts.compact }
  );

  return [{ id: provision.id, heading, subheading: proRataSubheading }];
}

export function createDispositiveProvisionRow({
  provision,
  nameOfDeadClient,
  nameOfSurvivingClient,
  isDeathOrderScoped,
  hasStateTax,
  calculatedProvisionAmount,
  direction,
  transferTaxKinds,
  proRataTemplateName,
}: {
  provision: DispositiveProvisions_DispositiveProvisionFragment;
  nameOfDeadClient: string;
  nameOfSurvivingClient: string;
  isDeathOrderScoped: boolean;
  hasStateTax: boolean;
  calculatedProvisionAmount: Decimal | null;
  direction: DispositiveProvisionDirection;
  transferTaxKinds?: DispositiveProvisionTransferTaxKind[];
  proRataTemplateName?: string;
}): DispositiveProvisionRow {
  const transferTaxKind = getTransferTaxKindOptions({
    hasStateTax,
  }).find((option) => option.value === provision.transferTaxKind)?.display;

  const groupedTransferTaxKinds = transferTaxKinds?.flatMap(
    (kind) =>
      getTransferTaxKindOptions({ hasStateTax }).find(
        (option) => option.value === kind
      )?.display ?? []
  );

  const getNotesTooltip = (
    provision: DispositiveProvisions_DispositiveProvisionFragment
  ) => {
    if (provision.notes) {
      return (
        <InfoTooltip
          title={<Box sx={{ whiteSpace: 'pre-wrap' }}>{provision.notes}</Box>}
          placement="top-start"
        />
      );
    }
    return null;
  };

  const taxKindItems = groupedTransferTaxKinds
    ? groupedTransferTaxKinds
    : compact([transferTaxKind]);

  const sharedProperties = {
    provisions: getDistributionDetails(provision, calculatedProvisionAmount, {
      proRataTemplateName,
    }),
    Tooltip: getNotesTooltip(provision),
  };

  const buildResult = (heading: string, entityTypeDescription: string) => {
    return {
      heading,
      description: entityTypeDescription,
      additionalItems: taxKindItems,
      ...sharedProperties,
    };
  };

  if (
    provision.recipientKind ===
      DispositiveProvisionRecipientKind.SurvivingSpouse &&
    direction === DispositiveProvisionDirection.Distributing
  ) {
    // Without a dying client (e.g. beneficiary designations) the resolved
    // surviving-client name is ambiguous, so fall back to a generic label.
    const survivingSpouseHeading = isDeathOrderScoped
      ? nameOfSurvivingClient
      : 'Surviving spouse';
    return buildResult(survivingSpouseHeading, 'Individual');
  } else if (provision.individual) {
    return buildResult(provision.individual.displayName, 'Individual');
  } else if (provision.entity) {
    return buildResult(
      provision.entity.subtype.displayName,
      entityKindToDisplayName(provision.entity.kind)
    );
  } else if (provision.testamentaryEntity) {
    return buildResult(
      provision.testamentaryEntity.displayName,
      testamentaryEntityKindToDisplayName(
        provision.testamentaryEntity.kind,
        nameOfDeadClient
      )
    );
  } else if (provision.organization) {
    return buildResult(provision.organization.name, 'Organization');
  }

  diagnostics.warn(
    `unknown dispositve provision type for provision ${provision.id}`,
    provision
  );

  return buildResult('', '');
}

interface DispositiveProvisionRowPrimaryClient {
  displayName: string;
  id: string;
}

interface DispositiveProvisionRowProps {
  provision: DispositiveProvisions_DispositiveProvisionFragment;
  dyingPrimaryClientId: string | null;
  primaryClients: DispositiveProvisionRowPrimaryClient[] | null;
  calculatedProvisionAmount: Decimal | null | undefined;
  direction: DispositiveProvisionDirection;
  transferTaxKinds?: DispositiveProvisionTransferTaxKind[];
  proRataTemplateName?: string;
}

export function DispositiveProvisionRow({
  provision,
  dyingPrimaryClientId,
  primaryClients,
  calculatedProvisionAmount,
  direction,
  transferTaxKinds,
  proRataTemplateName,
}: DispositiveProvisionRowProps) {
  const { availableStateEstateTaxes } = useIRSConstants();

  const { data: clientExemptionData } = useRemainingExemptionAmountForClient({
    // clientProfileId of who died
    clientId: dyingPrimaryClientId,
  });
  const showCalculatedDispositions =
    useUnguardedDispositiveProvisionsContext()?.showCalculatedDispositions;

  const nameOfDeadClient: string = useMemo(() => {
    return (
      primaryClients?.find((g) => g.id === dyingPrimaryClientId)?.displayName ??
      ''
    );
  }, [dyingPrimaryClientId, primaryClients]);
  const nameOfSurvivingClient: string = useMemo(() => {
    return (
      primaryClients?.find((g) => g.id !== dyingPrimaryClientId)?.displayName ??
      ''
    );
  }, [dyingPrimaryClientId, primaryClients]);

  const hasStateTax = useMemo(() => {
    if (!clientExemptionData) return false;

    return Boolean(
      availableStateEstateTaxes?.includes(
        clientExemptionData.stateCode as StateCode
      )
    );
  }, [availableStateEstateTaxes, clientExemptionData]);

  const rowData = createDispositiveProvisionRow({
    provision,
    nameOfDeadClient,
    nameOfSurvivingClient,
    isDeathOrderScoped: dyingPrimaryClientId != null,
    hasStateTax,
    calculatedProvisionAmount:
      (showCalculatedDispositions && calculatedProvisionAmount) || null,
    direction,
    transferTaxKinds,
    proRataTemplateName,
  });

  const {
    provisions,
    description: _entityType,
    ...richListItemProps
  } = rowData;

  return (
    <Box sx={{ containerType: 'inline-size' }}>
      <TableCell
        RightContent={
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={(theme) => ({
              py: 1,
              [containerQuery(theme).down(500)]: { maxWidth: 260 },
            })}
          >
            <Stack spacing={1}>
              {provisions.map(({ subheading, ...rest }) => (
                <Box key={rest.id} mb={1}>
                  <TableCellContext
                    {...rest}
                    subheading={proRataTemplateName ? subheading : undefined}
                  />
                </Box>
              ))}
            </Stack>
          </Stack>
        }
        {...richListItemProps}
      />
    </Box>
  );
}
